import * as React from 'react';

import type { Learner } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import type { LearnerListAudience } from 'bundles/enterprise-admin-messaging-v2/hooks/useAudienceLearners';
import {
  AUDIENCE_LEARNERS_PAGE_SIZE,
  useAudienceLearners,
} from 'bundles/enterprise-admin-messaging-v2/hooks/useAudienceLearners';

const SEARCH_DEBOUNCE_MS = 300;

type Params = {
  /** Absent for the non-filter (manual) flow — the query is then skipped. */
  audience?: LearnerListAudience;
};

type Result = {
  searchQuery: string;
  setSearchQuery: (next: string) => void;
  /** 1-indexed current page. */
  page: number;
  setPage: (next: number) => void;
  pageCount: number;
  /** The current page of learners as returned by the gateway. */
  learners: Learner[];
  /** Total rows matched by the current query (across all pages). */
  total: number;
  loading: boolean;
  /** True when the page fetch failed. */
  error: boolean;
};

/**
 * Shared page + (debounced) search state for the server-backed learner-list
 * hooks. Owns the gateway fetch via {@link useAudienceLearners} and the
 * out-of-range page clamp; callers layer their own row derivation (viewer:
 * read-only; editor: delta-aware) on top of `learners`.
 */
export const useServerLearnerPaging = ({ audience }: Params): Result => {
  const [searchQuery, setSearchQueryRaw] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [page, setPage] = React.useState(1);

  // Debounce the search term so each keystroke doesn't fire a query.
  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { learners, total, loading, error } = useAudienceLearners({
    audience,
    page,
    search: debouncedSearch,
  });

  const pageCount = Math.max(1, Math.ceil(total / AUDIENCE_LEARNERS_PAGE_SIZE));

  // Clamp an out-of-range page (e.g. after a search shrinks the result set) so
  // the cursor can't run past the data and desync from the displayed page.
  // Guard on `total > 0`: with `no-cache`, `total` is 0 mid-fetch, which would
  // otherwise snap a just-changed page back to 1.
  React.useEffect(() => {
    if (total > 0 && page > pageCount) setPage(pageCount);
  }, [page, pageCount, total]);

  const setSearchQuery = React.useCallback((next: string) => {
    setSearchQueryRaw(next);
    setPage(1);
  }, []);

  return { searchQuery, setSearchQuery, page, setPage, pageCount, learners, total, loading, error: Boolean(error) };
};

export default useServerLearnerPaging;
